import React, { useState } from "react";
import styles from "./UpgradeModal.module.css";
import type { TierLevel } from "../types/UserTypes";

interface UpgradeModalProps {
  featureName: string;
  requiredTier: TierLevel;
  currentTier: TierLevel;
  onUpgrade: (tier: TierLevel) => Promise<void>;
  canUpgrade?: boolean;
  upgradeActionLabel?: string;
  onClose: () => void;
}

export default function UpgradeModal({
  featureName,
  requiredTier,
  currentTier,
  onUpgrade,
  canUpgrade = true,
  upgradeActionLabel,
  onClose,
}: UpgradeModalProps) {
  const [loading, setLoading] = useState(false);

  const formatTier = (t: string) => t.replace(/_/g, " ");

  const handleUpgrade = async () => {
    if (!canUpgrade) return;
    setLoading(true);
    try {
      await onUpgrade(requiredTier);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgradeModalTitle"
      >
        <div className={styles.title} id="upgradeModalTitle">Upgrade Required</div>
        <div className={styles["body-text"]}>
          <strong>"{featureName}"</strong> is available on the{" "}
          <strong style={{ textTransform: "capitalize" }}>
            {formatTier(requiredTier)}
          </strong>{" "}
          plan.
        </div>
        <div className={styles["current-plan"]}>
          Your current plan: <strong>{formatTier(currentTier)}</strong>
        </div>
        {!canUpgrade && (
          <div className={styles["current-plan"]}>
            Only the account Owner can change subscription plans.
          </div>
        )}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles["cancel-btn"]}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles["upgrade-btn"]}
            onClick={handleUpgrade}
            disabled={loading || !canUpgrade}
          >
            {loading
              ? "Opening billing…"
              : upgradeActionLabel ?? `Upgrade to ${formatTier(requiredTier)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
